import React, { useEffect } from "react";
import { usePlayerSessions } from "@/hooks/usePlayerSessions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PurchasedMentorshipCard } from "@/components/player/PurchasedMentorshipCard";
import { EmptyState } from "@/components/player/EmptyState";

const SessoesCompradas = () => {
  // Reutilizar o hook que busca sessões por player_profile_id
  // Funciona independente do role atual do usuário
  const { sessions, loading, refetch } = usePlayerSessions();

  useEffect(() => {
    // Log sessions data for debugging
    if (sessions.length > 0) {
      console.log("📚 [SessoesCompradas - Mentor] Sessões carregadas:", sessions);
    }
  }, [sessions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  const upcomingSessions = sessions
    .filter((session) => 
      session.status === "confirmado" || session.status === "pendente"
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const completedSessions = sessions
    .filter((session) => 
      session.status === "concluido"
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Mentorias Compradas</h1>
          <p className="text-gray-400 mt-1">
            Histórico de mentorias que você adquiriu como jogador
          </p>
        </div>
        <Button 
          variant="outline" 
          size="icon"
          onClick={() => refetch(true)}
          className="h-9 w-9 self-start sm:self-auto"
          title="Atualizar mentorias"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex flex-col items-center">
              <div className="text-2xl font-bold text-white mb-1">{sessions.length}</div>
              <div className="text-sm text-gray-400">Total de Sessões</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex flex-col items-center">
              <div className="text-2xl font-bold text-[#00B6F0]">{upcomingSessions.length}</div>
              <div className="text-sm text-gray-400">Próximas Sessões</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex flex-col items-center">
              <div className="text-2xl font-bold text-green-400">{completedSessions.length}</div>
              <div className="text-sm text-gray-400">Sessões Concluídas</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {sessions.length === 0 ? (
        <EmptyState 
          title="Nenhuma mentoria comprada" 
          description="Você ainda não comprou nenhuma mentoria. Explore mentores e jogos para encontrar a mentoria ideal para você."
          actionLabel="Explorar Mentorias"
          actionHref="/mentorias"
        />
      ) : (
        <>
          {/* Upcoming Sessions */}
          <Card className="bg-[#1A2233] border-[#222A35] text-white mb-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Próximas Sessões</CardTitle>
              {upcomingSessions.length > 0 && (
                <Badge variant="default" className="bg-[#00B6F0]">
                  {upcomingSessions.length}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {upcomingSessions.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {upcomingSessions.map((session) => (
                    <PurchasedMentorshipCard 
                      key={session.id} 
                      session={session} 
                      onActionCompleted={refetch}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-gray-400">Nenhuma sessão agendada</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Past Sessions */}
          <Card className="bg-[#1A2233] border-[#222A35] text-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Sessões Concluídas</CardTitle>
              {completedSessions.length > 0 && (
                <Badge variant="outline">
                  {completedSessions.length}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {completedSessions.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {completedSessions.map((session) => (
                    <PurchasedMentorshipCard 
                      key={session.id} 
                      session={session} 
                      onActionCompleted={refetch}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-gray-400">Nenhuma sessão concluída</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SessoesCompradas;
